export type MarkdownFormatMode =
  | 'heading'
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'codeBlock'
  | 'link'
  | 'bullet'
  | 'number'
  | 'quote'
  | 'image'
  | 'table'
  | 'divider';

export interface MarkdownEditResult {
  readonly value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
}

export interface MarkdownFormatFallbackMessages {
  readonly heading: string;
  readonly listItem: string;
  readonly quote: string;
  readonly code: string;
  readonly linkText: string;
  readonly imageDescription: string;
  readonly tableColumnA: string;
  readonly tableColumnB: string;
  readonly tableValue: string;
  readonly boldText: string;
  readonly italicText: string;
  readonly struckText: string;
}

export type MarkdownFormatTranslate = (key: string) => string;

export function markdownFormatFallbacks(
  t: MarkdownFormatTranslate,
): MarkdownFormatFallbackMessages {
  return {
    heading: t('primitives.markdown.formatFallback.heading'),
    listItem: t('primitives.markdown.formatFallback.listItem'),
    quote: t('primitives.markdown.formatFallback.quote'),
    code: t('primitives.markdown.formatFallback.code'),
    linkText: t('primitives.markdown.formatFallback.linkText'),
    imageDescription: t('primitives.markdown.formatFallback.imageDescription'),
    tableColumnA: t('primitives.markdown.formatFallback.tableColumnA'),
    tableColumnB: t('primitives.markdown.formatFallback.tableColumnB'),
    tableValue: t('primitives.markdown.formatFallback.tableValue'),
    boldText: t('primitives.markdown.formatFallback.boldText'),
    italicText: t('primitives.markdown.formatFallback.italicText'),
    struckText: t('primitives.markdown.formatFallback.struckText'),
  };
}

export type RichTextPasteVariant = 'description' | 'comment';

export interface RichTextPasteInput {
  readonly value: string;
  readonly html: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  readonly variant: RichTextPasteVariant;
  readonly maxLength?: number | undefined;
}

export interface RichTextPasteResult extends MarkdownEditResult {
  readonly handled: boolean;
}

export function formatMarkdown(
  fallbacks: MarkdownFormatFallbackMessages,
  value: string,
  start: number,
  end: number,
  selected: string,
  mode: MarkdownFormatMode,
): MarkdownEditResult {
  if (mode === 'heading') {
    const text = selected.length === 0 ? fallbacks.heading : selected;
    return insertAt(value, start, end, `## ${text}`, start + 3, start + 3 + text.length);
  }

  if (mode === 'divider') {
    const prefix = start > 0 && !value.slice(0, start).endsWith('\n') ? '\n' : '';
    const insertion = `${prefix}---\n`;
    return insertAt(
      value,
      start,
      end,
      insertion,
      start + insertion.length,
      start + insertion.length,
    );
  }

  if (mode === 'bullet' || mode === 'number' || mode === 'quote') {
    const fallback =
      mode === 'bullet'
        ? fallbacks.listItem
        : mode === 'number'
          ? fallbacks.listItem
          : fallbacks.quote;
    const text = selected.length === 0 ? fallback : selected;
    const formatted = text
      .split('\n')
      .map((line, index) => {
        if (mode === 'bullet') return `- ${line}`;
        if (mode === 'number') return `${(index + 1).toString()}. ${line}`;
        return `> ${line}`;
      })
      .join('\n');
    return insertAt(value, start, end, formatted, start, start + formatted.length);
  }

  if (mode === 'codeBlock') {
    const text = selected.length === 0 ? fallbacks.code : selected;
    const insertion = `\`\`\`\n${text}\n\`\`\``;
    return insertAt(value, start, end, insertion, start + 4, start + 4 + text.length);
  }

  if (mode === 'link') {
    const text = selected.length === 0 ? fallbacks.linkText : selected;
    const insertion = `[${text}](https://example.com)`;
    return insertAt(value, start, end, insertion, start + 1, start + 1 + text.length);
  }

  if (mode === 'image') {
    const text = selected.length === 0 ? fallbacks.imageDescription : selected;
    const insertion = `![${text}](https://example.com/image.png)`;
    return insertAt(value, start, end, insertion, start + 2, start + 2 + text.length);
  }

  if (mode === 'table') {
    const insertion = `| ${fallbacks.tableColumnA} | ${fallbacks.tableColumnB} |\n| --- | --- |\n| ${fallbacks.tableValue} | ${fallbacks.tableValue} |`;
    return insertAt(value, start, end, insertion, start, start + insertion.length);
  }

  const fallback =
    mode === 'bold'
      ? fallbacks.boldText
      : mode === 'italic'
        ? fallbacks.italicText
        : mode === 'strike'
          ? fallbacks.struckText
          : fallbacks.code;
  const text = selected.length === 0 ? fallback : selected;
  const [before, after] =
    mode === 'bold'
      ? ['**', '**']
      : mode === 'italic'
        ? ['_', '_']
        : mode === 'strike'
          ? ['~~', '~~']
          : ['`', '`'];
  const insertion = `${before}${text}${after}`;
  return insertAt(
    value,
    start,
    end,
    insertion,
    start + before.length,
    start + before.length + text.length,
  );
}

function insertAt(
  value: string,
  start: number,
  end: number,
  insertion: string,
  selectionStart: number,
  selectionEnd: number,
): MarkdownEditResult {
  return {
    value: `${value.slice(0, start)}${insertion}${value.slice(end)}`,
    selectionStart,
    selectionEnd,
  };
}

export function htmlToMarkdown(
  html: string,
  variant: RichTextPasteVariant = 'description',
): string {
  const document = new DOMParser().parseFromString(html, 'text/html');
  return nodesToMarkdown(Array.from(document.body.childNodes), {
    variant,
    listStack: [],
  }).trim();
}

export function applyRichTextPaste(input: RichTextPasteInput): RichTextPasteResult {
  if (input.html.trim() === '') {
    return {
      handled: false,
      value: input.value,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd,
    };
  }

  const pasted = htmlToMarkdown(input.html, input.variant);
  const uncapped = `${input.value.slice(0, input.selectionStart)}${pasted}${input.value.slice(input.selectionEnd)}`;
  const value =
    input.maxLength === undefined ? uncapped : uncapped.slice(0, input.maxLength);
  const cursor = Math.min(input.selectionStart + pasted.length, value.length);

  return {
    handled: true,
    value,
    selectionStart: cursor,
    selectionEnd: cursor,
  };
}

interface MarkdownHtmlContext {
  readonly variant: RichTextPasteVariant;
  readonly listStack: readonly ('ul' | 'ol')[];
}

function nodesToMarkdown(nodes: Node[], context: MarkdownHtmlContext): string {
  return nodes.map((node) => nodeToMarkdown(node, context)).join('');
}

function nodeToMarkdown(node: Node, context: MarkdownHtmlContext): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (!(node instanceof HTMLElement)) return '';

  const tag = node.tagName.toLowerCase();
  const children = nodesToMarkdown(Array.from(node.childNodes), context);
  switch (tag) {
    case 'strong':
    case 'b':
      return `**${children}**`;
    case 'em':
    case 'i':
      return `_${children}_`;
    case 's':
    case 'strike':
    case 'del':
      return `~~${children}~~`;
    case 'code':
      return `\`${children}\``;
    case 'a': {
      const href = safeMarkdownUrl(node.getAttribute('href'));
      return href === null ? children : `[${children}](${href})`;
    }
    case 'h1':
      return context.variant === 'comment' ? `${children}\n\n` : `# ${children}\n\n`;
    case 'h2':
      return context.variant === 'comment' ? `${children}\n\n` : `## ${children}\n\n`;
    case 'h3':
      return context.variant === 'comment' ? `${children}\n\n` : `### ${children}\n\n`;
    case 'blockquote':
      return `${children
        .split('\n')
        .filter((line) => line.length > 0)
        .map((line) => `> ${line}`)
        .join('\n')}\n\n`;
    case 'li': {
      const depth = Math.max(0, context.listStack.length - 1);
      const parent = context.listStack[context.listStack.length - 1] ?? 'ul';
      const marker = parent === 'ol' ? '1.' : '-';
      return `${'  '.repeat(depth)}${marker} ${children.trim()}\n`;
    }
    case 'ul':
    case 'ol':
      return `${nodesToMarkdown(Array.from(node.childNodes), {
        ...context,
        listStack: [...context.listStack, tag],
      })}\n`;
    case 'pre': {
      const text = node.textContent ?? '';
      return `\`\`\`\n${text.replace(/\n$/, '')}\n\`\`\`\n\n`;
    }
    case 'img': {
      const alt = node.getAttribute('alt') ?? '';
      const src = safeMarkdownUrl(node.getAttribute('src'));
      if (context.variant === 'comment') return alt.length === 0 ? '' : `${alt}\n\n`;
      return src === null ? alt : `![${alt}](${src})`;
    }
    case 'table':
      return tableToPlainText(node);
    case 'p':
    case 'div':
      return `${children}\n\n`;
    case 'br':
      return '\n';
    default:
      return children;
  }
}

function tableToPlainText(table: HTMLElement): string {
  return Array.from(table.querySelectorAll('tr'))
    .map((row) =>
      Array.from(row.querySelectorAll('th,td'))
        .map((cell) => cell.textContent?.trim() ?? '')
        .filter((cell) => cell.length > 0)
        .join(' | '),
    )
    .filter((row) => row.length > 0)
    .join('\n');
}

function safeMarkdownUrl(value: string | null): string | null {
  if (value === null) return null;
  if (/^(https?:|mailto:|tel:|\/(?!\/))/i.test(value)) return value;
  return null;
}
